import { onScopeDispose, watch } from 'vue';

import type SkillSuggestionsService from './skill-suggestions.service';
import { hasUsableContent, isSubstantialDescription } from './skill-suggestions.utils';

export const SKILL_SUGGESTIONS_TRIGGER_DEBOUNCE_MS = 2000;

type Options = {
  service: Pick<SkillSuggestionsService, 'request'>;
  isCreatingNewJob: () => boolean;
  canEditForm: () => boolean;
  isSkillsSectionBlurred: () => boolean;
  isJobLoaded: () => boolean;
  getContent: () => { title: string | null; description: string | null };
  debounceMs?: number;
};

// When to ask for suggestions. Everything is gated on the form being editable, and on content
// that is settled: new jobs start early once, then Continue / reaching the skills section and
// the debounced content watcher keep suggestions current. The service dedupes by fingerprint.
export function useSkillSuggestionTriggers(options: Options): { requestOnContinue: () => void } {
  const debounceMs = options.debounceMs ?? SKILL_SUGGESTIONS_TRIGGER_DEBOUNCE_MS;
  let earlyRequestMade = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function request() {
    const { title, description } = options.getContent();
    options.service.request(title, description);
  }

  function cancelPending() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  // Re-evaluated at fire time as well, because editability and the section state can change
  // while a debounce is pending.
  function requestWhenReady() {
    if (!options.isJobLoaded() || !options.canEditForm()) {
      return;
    }
    if (!options.isSkillsSectionBlurred()) {
      request();
      return;
    }
    const { title, description } = options.getContent();
    if (
      options.isCreatingNewJob() &&
      !earlyRequestMade &&
      hasUsableContent(title, description) &&
      isSubstantialDescription(description)
    ) {
      earlyRequestMade = true;
      request();
    }
  }

  watch([options.isJobLoaded, options.canEditForm], requestWhenReady, { immediate: true });

  watch(options.canEditForm, (canEdit) => {
    if (!canEdit) {
      cancelPending();
    }
  });

  watch(options.isSkillsSectionBlurred, (blurred) => {
    if (!blurred && options.canEditForm()) {
      request();
    }
  });

  watch(
    () => {
      const { title, description } = options.getContent();
      return [title, description];
    },
    () => {
      if (!options.isJobLoaded() || !options.canEditForm()) {
        return;
      }
      cancelPending();
      timer = setTimeout(() => {
        timer = null;
        requestWhenReady();
      }, debounceMs);
    },
  );

  onScopeDispose(cancelPending);

  return {
    requestOnContinue() {
      if (options.canEditForm()) {
        request();
      }
    },
  };
}
